import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const brandButtonVariants = cva(
	"inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-semibold rounded-[6px] transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				primary: "bg-brand-accent text-white hover:bg-brand-accent/90",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80",
				outline:
					"[box-shadow:rgba(0,0,0,0.08)_0px_0px_0px_1px] bg-transparent text-foreground hover:bg-muted dark:[box-shadow:rgba(255,255,255,0.08)_0px_0px_0px_1px]",
				ghost: "text-foreground hover:bg-muted",
				destructive:
					"bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/40",
			},
			size: {
				default: "h-9 px-4 py-2",
				sm: "h-8 px-3 text-[13px]",
				lg: "h-11 px-6 text-base",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "default",
		},
	},
);

export interface BrandButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof brandButtonVariants> {
	asChild?: boolean;
}

const BrandButton = React.forwardRef<HTMLButtonElement, BrandButtonProps>(
	({ className, variant, size, asChild, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(brandButtonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
BrandButton.displayName = "BrandButton";

export { BrandButton, brandButtonVariants };
